import React, { FC, Fragment, MouseEvent, useEffect, useState } from 'react';
import { Box, Button, Popover, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { FiltersButton } from 'features/admin/components/shared';
import { AdvancedFilterInput } from './index';
import {
  CompaniesFilter,
  CompaniesQueryParams,
  PeopleFilter,
  PeopleQueryParams,
} from 'features/insights/types';
import { hasReachedMaxQueries } from 'features/insights/helpers';
import theme from 'theme';
import { AdvancedFilters } from 'api';

interface IProps {
  filter: PeopleFilter | CompaniesFilter;
  setFilter: (filter: PeopleQueryParams | CompaniesQueryParams) => void;
}

const styles = {
  container: {
    width: 700,
    maxWidth: 700,
    padding: theme.spacing(4),
  },
  submitButton: {
    marginLeft: theme.spacing(2),
  },
} as const;

const AdvancedFiltersModal: FC<IProps> = ({ filter, setFilter }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [tempFilter, setTempFilter] = useState(filter);
  const { t } = useTranslation();

  useEffect(() => {
    setTempFilter(filter);
  }, [filter]);

  function onClickButton(event: MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function handleSubmit() {
    setFilter({
      ...tempFilter,
      sort_by: JSON.stringify(filter.sort_by),
      users: tempFilter?.users?.join(',') || '',
      posts: tempFilter?.posts?.join(',') || '',
      company_pages: tempFilter?.company_pages?.join(',') || '',
    });

    handleClose();
  }

  return (
    <Fragment>
      <FiltersButton onClick={onClickButton} />
      <Popover
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
      >
        <Box sx={styles.container}>
          <Grid container spacing={4}>
            <Grid lg={12} container justifyContent="flex-end" alignItems="center" spacing={2}>
              <Grid lg={3}>
                <Typography variant="body2">{t('insights:users_advanced_filter_title')}</Typography>
              </Grid>
              <Grid lg={9}>
                <AdvancedFilterInput
                  tempFilter={tempFilter}
                  setTempFilter={setTempFilter}
                  filterField={AdvancedFilters.Users}
                />
              </Grid>
              <Grid lg={3}>
                <Typography variant="body2">{t('insights:posts_advanced_filter_title')}</Typography>
              </Grid>
              <Grid lg={9}>
                <AdvancedFilterInput
                  tempFilter={tempFilter}
                  setTempFilter={setTempFilter}
                  filterField={AdvancedFilters.Posts}
                />
              </Grid>
              <Grid lg={3}>
                <Typography variant="body2">
                  {t('insights:company_pages_advanced_filter_title')}
                </Typography>
              </Grid>
              <Grid lg={9}>
                <AdvancedFilterInput
                  tempFilter={tempFilter}
                  setTempFilter={setTempFilter}
                  filterField={AdvancedFilters.CompanyPages}
                />
              </Grid>
            </Grid>
            <Grid lg={12}>
              <Grid container alignItems="center" justifyContent="flex-end" spacing={2}>
                <Grid>
                  <Button variant="outlined" onClick={handleClose} size="large">
                    {t('common:cancel')}
                  </Button>
                </Grid>
                <Grid>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    size="large"
                    disabled={hasReachedMaxQueries(tempFilter)}
                  >
                    {t('common:apply')}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Popover>
    </Fragment>
  );
};

export default AdvancedFiltersModal;
